/* eslint-disable max-len */
import React, { useContext, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Formik, Form, Field } from 'formik';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Panel } from 'primereact/panel';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { growlErrors, growlActionSuccess } from '../../../utils/NotificationUtil';
import { FormikCalendar, InputTextComponent } from '../../../components/prime-formik';
import { AccountContext } from '../../../components/AccountContext';
import { CreditPlan } from '../../../shared/Constants';

const i18n = defineMessages({
  creditPlans: { id: 'menu.credit_plans', defaultMessage: 'Credit Plan: ' },
  unlimitedCreditPlanExpirationDate: {
    id: 'unlimited-credit-plan.expiration-date',
    defaultMessage: 'Unlimited credit plan expiration date: ',
  },
  numberOfInternalRespondents: {
    id: 'accept-client-account.org-step.number-of-internal-respondents',
    defaultMessage: 'Number of internal respondents',
  },
  accounts: { id: 'menu.accounts', defaultMessage: 'Account: ' },
  save: { id: 'menu.save', defaultMessage: 'Save' },
});

const CreditPlanForm = ({ accounts, updateCreditPlanMutation }) => {
  const [hidden, setHidden] = useState(true);
  const [accountsForDropdown, setAccountsForDropdown] = useState([]);

  const { formatMessage } = useIntl();
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);

  useEffect(() => {
    const unlimitedAccounts = accounts.filter((account) => account.creditPlan === CreditPlan.UNLIMITED);
    setAccountsForDropdown(unlimitedAccounts);
    // eslint-disable-next-line
  }, []);

  const updateCreditPlan = async (values, resetForm) => {
    try {
      const { targetAccountId, newCreditPlan, unlimitedCreditPlanExpirationDate, numberOfInternalRespondents } = values;

      let expirationDate;

      if (newCreditPlan === CreditPlan.STANDARD) {
        expirationDate = unlimitedCreditPlanExpirationDate;
      } else {
        expirationDate = new Date(
          Date.UTC(
            unlimitedCreditPlanExpirationDate.getFullYear(),
            unlimitedCreditPlanExpirationDate.getMonth(),
            unlimitedCreditPlanExpirationDate.getDate(),
          ),
        );
      }

      await updateCreditPlanMutation({
        variables: {
          input: {
            gpiAccountId: currentAccount,
            targetAccountId,
            newCreditPlan,
            unlimitedCreditPlanExpirationDate: expirationDate,
            numberOfInternalRespondents,
          },
        },
        refetchQueries: ['NewCreditPlanQuery'],
      });

      resetForm();
      growlActionSuccess(growl);
    } catch (err) {
      resetForm();
      growlErrors(growl, err);
    } finally {
      setHidden(true);
    }
  };

  const oneYearFromNow = new Date();
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

  const creditPlansOptions = [
    {
      label: 'Standard credits consumption',
      value: CreditPlan.STANDARD,
    },
    {
      label: 'Unlimited Credits',
      value: CreditPlan.UNLIMITED,
    },
  ];

  const accountOptions = accountsForDropdown
    .filter((a) => a.id !== currentAccount)
    .map((a) => ({
      label: `${a.displayName} - ${a.id}`,
      value: a.id,
    }));

  const calendarInput = () => (
    <div className="p-col-12" hidden={hidden}>
      <label>{formatMessage(i18n.unlimitedCreditPlanExpirationDate)}</label>
      <Field
        name="unlimitedCreditPlanExpirationDate"
        id="unlimitedCreditPlanExpirationDate"
        component={FormikCalendar}
        dateFormat="yy-M-dd"
      />
    </div>
  );

  const getPreSelected = (accountOptions) => {
    if (!accountOptions.length > 0) {
      return null;
    }

    return accountOptions[0].value;
  };

  const preSelected = getPreSelected(accountOptions);

  const initialValues = {
    targetAccountId: preSelected,
    newCreditPlan: CreditPlan.STANDARD,
    unlimitedCreditPlanExpirationDate: oneYearFromNow,
    numberOfInternalRespondents: 1,
  };

  const validationSchema = Yup.object().shape({
    targetAccountId: Yup.string().required(),
    newCreditPlan: Yup.string().required(),
    unlimitedCreditPlanExpirationDate: Yup.string().when('newCreditPlan', {
      is: (val) => val === CreditPlan.UNLIMITED,
      then: Yup.string().required(),
      otherwise: Yup.string(),
    }),
    numberOfInternalRespondents: Yup.number().when('newCreditPlan', {
      is: (val) => val === CreditPlan.UNLIMITED,
      then: Yup.number().required(),
      otherwise: Yup.number(),
    }),
  });

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <Panel>
            <div className="p-grid">
              <div className="p-sm-12 p-md-6">
                <Formik
                  validationSchema={validationSchema}
                  initialValues={initialValues}
                  onSubmit={(values, formikBag) => {
                    const { resetForm } = formikBag;
                    updateCreditPlan(values, resetForm);
                  }}
                >
                  {({ isSubmitting, handleChange, setFieldValue, values }) => (
                    <Form>
                      <div className="p-grid p-fluid">
                        <div className="p-col-12">
                          <label>{formatMessage(i18n.accounts)}</label>
                          <Field
                            component={Dropdown}
                            id="targetAccountId"
                            name="targetAccountId"
                            key="targetAccountId"
                            value={values.targetAccountId}
                            required
                            filter
                            options={accountOptions}
                            onChange={(e) => {
                              const account = accountsForDropdown.filter(
                                (accountInList) => accountInList.id === e.target.value,
                              )[0];

                              if (account.numberOfInternalRespondents) {
                                setFieldValue('numberOfInternalRespondents', account.numberOfInternalRespondents);
                              }

                              if (account.unlimitedCreditPlanExpirationDate) {
                                setFieldValue(
                                  'unlimitedCreditPlanExpirationDate',
                                  account.unlimitedCreditPlanExpirationDate,
                                );
                              } else {
                                setFieldValue('unlimitedCreditPlanExpirationDate', oneYearFromNow);
                              }

                              handleChange(e);
                            }}
                          />
                        </div>
                        <div className="p-col-12">
                          <label>{formatMessage(i18n.creditPlans)}</label>
                          <Field
                            component={Dropdown}
                            id="newCreditPlan"
                            name="newCreditPlan"
                            key="newCreditPlan"
                            value={values.newCreditPlan}
                            required
                            options={creditPlansOptions}
                            onChange={(e) => {
                              if (e.target.value === CreditPlan.UNLIMITED) {
                                const standardAccounts = accounts.filter(
                                  (account) => account.creditPlan === CreditPlan.STANDARD,
                                );

                                const account = standardAccounts[0];

                                if (account.unlimitedCreditPlanExpirationDate) {
                                  setFieldValue(
                                    'unlimitedCreditPlanExpirationDate',
                                    account.unlimitedCreditPlanExpirationDate,
                                  );
                                }

                                if (account.numberOfInternalRespondents) {
                                  setFieldValue('numberOfInternalRespondents', account.numberOfInternalRespondents);
                                }

                                setHidden(false);
                                setAccountsForDropdown(standardAccounts);

                                setFieldValue('targetAccountId', account.id);
                              } else {
                                const unlimitedAccounts = accounts.filter(
                                  (account) => account.creditPlan === CreditPlan.UNLIMITED,
                                );

                                const account = unlimitedAccounts[0];

                                setHidden(true);
                                setAccountsForDropdown(unlimitedAccounts);

                                setFieldValue('targetAccountId', account.id);
                              }

                              handleChange(e);
                            }}
                          />
                        </div>
                        {values.newCreditPlan === CreditPlan.UNLIMITED && (
                          <div className="p-col-12">
                            <label>{formatMessage(i18n.numberOfInternalRespondents)}</label>
                            <Field name="numberOfInternalRespondents" type="number" component={InputTextComponent} />
                          </div>
                        )}
                        {calendarInput()}
                      </div>
                      <div className="p-grid">
                        <div className="p-col-12">
                          <Button label={formatMessage(i18n.save)} type="submit" disabled={isSubmitting} />
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};

CreditPlanForm.propTypes = {
  accounts: PropTypes.arrayOf(PropTypes.any).isRequired,
  updateCreditPlan: PropTypes.func.isRequired,
};

export default CreditPlanForm;
